import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { AppSettings, AttendanceMonth, AttendanceSummaryRow, LeaveAdjustment, LeaveApplication, LeaveBankEntry, LeaveBankLedger, LeaveStatus, LeaveType, SalaryRow, AuditLogEntry, AuthUser, CalendarException, CalendarExceptionKind, DashboardOverview, Paginated, Project, ReportResponse, Screenshot, TimeEntry } from '@chronos/shared';
import { apiFetch } from '@/api/client';
import { desktop } from '@/lib/desktop';

/* ---------------------------------------------------------------- projects */

export function useMyProjects() {
  return useQuery({
    queryKey: ['projects', 'mine'],
    queryFn: () => apiFetch<Project[]>('/api/projects/mine'),
  });
}

export function useProjects(params: { q?: string; status?: string; page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['projects', params],
    queryFn: () => apiFetch<Paginated<Project>>('/api/projects', { query: params }),
  });
}

/* ------------------------------------------------------------ time entries */

export function useCurrentEntry(options?: Partial<UseQueryOptions<TimeEntry | null>>) {
  return useQuery<TimeEntry | null>({
    queryKey: ['time-entries', 'current'],
    queryFn: () => apiFetch<TimeEntry | null>('/api/time-entries/current'),
    // Keeps the header timer honest if the user clocks in from another device.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useTimeEntries(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ['time-entries', params],
    queryFn: () => apiFetch<Paginated<TimeEntry>>('/api/time-entries', { query: params }),
  });
}

export interface TimesheetSummary {
  from: string;
  to: string;
  totalSeconds: number;
  byDay: { date: string; seconds: number }[];
  byProject: { projectId: string; projectName: string; color: string; seconds: number }[];
  entries: TimeEntry[];
}

export function useTimesheet(from: string, to: string, userId?: string) {
  return useQuery({
    queryKey: ['timesheet', from, to, userId],
    queryFn: () => apiFetch<TimesheetSummary>('/api/time-entries/summary', { query: { from, to, userId } }),
  });
}

/** Invalidates everything a clock-in/out affects. */
export function useTimerActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['time-entries'] });
    void qc.invalidateQueries({ queryKey: ['timesheet'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    // Let the desktop shell pick up the new timer state at once rather than
    // waiting for its next poll, so capture starts/stops immediately.
    void desktop()?.notifyTimerChanged();
  };

  const start = useMutation({
    mutationFn: (input: { projectId: string; description?: string | null; source?: 'WEB' | 'DESKTOP' }) =>
      apiFetch<TimeEntry>('/api/time-entries/start', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });

  const stop = useMutation({
    mutationFn: (id: string) => apiFetch<TimeEntry>(`/api/time-entries/${id}/stop`, { method: 'POST', body: {} }),
    onSuccess: invalidate,
  });

  const createManual = useMutation({
    mutationFn: (input: { projectId: string; startTime: string; endTime: string; description?: string | null; userId?: string }) =>
      apiFetch<TimeEntry>('/api/time-entries', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiFetch<TimeEntry>(`/api/time-entries/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/time-entries/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  return { start, stop, createManual, update, remove };
}

/* ------------------------------------------------------------------ users */

export function useUsers(params: { q?: string; status?: string; role?: string; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => apiFetch<Paginated<AuthUser>>('/api/users', { query: { pageSize: 200, ...params } }),
  });
}

export function useUserProjects(userId: string | undefined) {
  return useQuery({
    queryKey: ['users', userId, 'projects'],
    queryFn: () => apiFetch<{ id: string; name: string; color: string; status: 'ACTIVE' | 'ARCHIVED'; isManager: boolean }[]>(`/api/users/${userId}/projects`),
    enabled: !!userId,
  });
}

/* -------------------------------------------------------------- dashboard */

export function useDashboard(from?: string, to?: string) {
  return useQuery({
    queryKey: ['dashboard', from, to],
    queryFn: () => apiFetch<DashboardOverview & { range: { from: string; to: string } }>('/api/dashboard/overview', { query: { from, to } }),
    refetchInterval: 60_000,
  });
}

/* ---------------------------------------------------------------- reports */

export function useReport(params: { from: string; to: string; userIds?: string[]; projectIds?: string[]; groupBy?: string }) {
  return useQuery({
    queryKey: ['report', params],
    queryFn: () => apiFetch<ReportResponse>('/api/reports/summary', { query: params }),
  });
}

/* ------------------------------------------------------------ screenshots */

export function useScreenshots(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ['screenshots', params],
    queryFn: () => apiFetch<Paginated<Screenshot>>('/api/screenshots', { query: params }),
  });
}

export interface GalleryBucket {
  userId: string;
  userName: string;
  bucketStart: string;
  items: Screenshot[];
}

export function useScreenshotGallery(params: { date: string; userId?: string; projectId?: string; intervalMinutes?: number }) {
  return useQuery({
    queryKey: ['screenshots', 'gallery', params],
    queryFn: () => apiFetch<{ date: string; intervalMinutes: number; buckets: GalleryBucket[] }>('/api/screenshots/gallery/by-interval', { query: params }),
  });
}

/* --------------------------------------------------------------- settings */

export function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: () => apiFetch<AppSettings>('/api/settings') });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<AppSettings>) => apiFetch<AppSettings>('/api/settings', { method: 'PATCH', body: patch }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

/* ------------------------------------------------------------ audit trail */

export function useAuditLogs(params: { action?: string; entityType?: string; page?: number } = {}) {
  return useQuery({
    queryKey: ['audit-logs', params],
    queryFn: () => apiFetch<Paginated<AuditLogEntry>>('/api/audit-logs', { query: { pageSize: 100, ...params } }),
  });
}

/* ------------------------------------------------------------- attendance */

export function useAttendance(month: string | undefined, userId?: string) {
  return useQuery({
    queryKey: ['attendance', month, userId],
    queryFn: () => apiFetch<AttendanceMonth>('/api/attendance', { query: { month, userId } }),
  });
}

export function useLeaveBank(month: string | undefined, userId?: string) {
  return useQuery({
    queryKey: ['attendance', 'leave-bank', month, userId],
    queryFn: () => apiFetch<LeaveBankLedger>('/api/attendance/leave-bank', { query: { month, userId } }),
  });
}

export function useAttendanceSummary(month: string | undefined) {
  return useQuery({
    queryKey: ['attendance', 'summary', month],
    queryFn: () => apiFetch<{ month: string; rows: AttendanceSummaryRow[] }>('/api/attendance/summary', { query: { month } }),
  });
}

export function useCalendar(year: number) {
  return useQuery({
    queryKey: ['calendar', year],
    queryFn: () => apiFetch<CalendarException[]>('/api/calendar', { query: { year } }),
  });
}

export function useCalendarMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['calendar'] });
    void qc.invalidateQueries({ queryKey: ['attendance'] });
  };
  const upsert = useMutation({
    mutationFn: (input: { date: string; kind: CalendarExceptionKind; name: string }) =>
      apiFetch<CalendarException>('/api/calendar', { method: 'PUT', body: input }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/calendar/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
  return { upsert, remove };
}

/* ----------------------------------------------------------------- salary */

export type SalarySettings = Pick<
  AppSettings,
  | 'currency'
  | 'attendanceBonusNoLeave'
  | 'attendanceBonusOneLeave'
  | 'attendanceBonusTwoPlusLeaves'
  | 'paidLeavesPerMonth'
  | 'halfDayLeaveWeight'
  | 'leaveYearEndMonth'
  | 'encashLeavesAtYearEnd'
  | 'deficitLeaveBands'
>;

export function useSalarySheet(month: string | undefined) {
  return useQuery({
    queryKey: ['salary', 'sheet', month],
    queryFn: () => apiFetch<{ month: string; currency: string; rows: SalaryRow[] }>('/api/salary/sheet', { query: { month } }),
  });
}

export function useSalarySettings() {
  return useQuery({ queryKey: ['salary', 'settings'], queryFn: () => apiFetch<SalarySettings>('/api/salary/settings') });
}

export function useSalaryMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['salary'] });
    void qc.invalidateQueries({ queryKey: ['attendance'] });
  };
  const setSalary = useMutation({
    mutationFn: ({ userId, ...body }: { userId: string; monthlySalary?: number | null; joinedOn?: string | null }) =>
      apiFetch<{ userId: string; monthlySalary: number | null; joinedOn: string | null }>(`/api/salary/employees/${userId}`, { method: 'PUT', body }),
    onSuccess: invalidate,
  });
  const addBankGrant = useMutation({
    mutationFn: (input: { userId: string; month: string; days: number; reason: string }) =>
      apiFetch<LeaveBankEntry>('/api/salary/bank', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
  const removeBankGrant = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/salary/bank/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
  const addAdjustment = useMutation({
    mutationFn: (input: { userId: string; month: string; days: number; reason: string }) =>
      apiFetch<LeaveAdjustment>('/api/salary/adjustments', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
  const removeAdjustment = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/salary/adjustments/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
  const updateSettings = useMutation({
    mutationFn: (patch: Partial<SalarySettings>) => apiFetch<SalarySettings>('/api/salary/settings', { method: 'PATCH', body: patch }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['salary'] }),
  });
  return { setSalary, addAdjustment, removeAdjustment, addBankGrant, removeBankGrant, updateSettings };
}

/* ------------------------------------------------------------------ leave */

export function useLeaveApplications(params: { userId?: string; status?: LeaveStatus; month?: string; team?: boolean } = {}) {
  return useQuery({
    queryKey: ['leave', params],
    queryFn: () => apiFetch<LeaveApplication[]>('/api/leave', { query: { ...params, team: params.team ? 'true' : undefined } }),
  });
}

export function useLeaveMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['leave'] });
    void qc.invalidateQueries({ queryKey: ['attendance'] });
    void qc.invalidateQueries({ queryKey: ['salary'] });
  };
  const apply = useMutation({
    // `userId` is admin-only: it files the leave for that employee, approved.
    mutationFn: (input: { type: LeaveType; from: string; to: string; halfDay: boolean; reason: string; userId?: string }) =>
      apiFetch<LeaveApplication>('/api/leave', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => apiFetch<LeaveApplication>(`/api/leave/${id}/cancel`, { method: 'POST' }),
    onSuccess: invalidate,
  });
  const decide = useMutation({
    mutationFn: (input: { id: string; status: 'APPROVED' | 'REJECTED'; note?: string | null }) =>
      apiFetch<LeaveApplication>(`/api/leave/${input.id}/decide`, { method: 'POST', body: { status: input.status, note: input.note ?? null } }),
    onSuccess: invalidate,
  });
  return { apply, cancel, decide };
}
